import { ChevronDown, Database } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { oneLight, SyntaxHighlighter } from '@/_shared/lib/syntax';

import type { Workflow } from '../types';

export function QuerySection({ workflow }: { workflow: Workflow }): React.ReactElement {
  const [sqlOpen, setSqlOpen] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-muted-foreground" />
          <CardTitle className="text-sm">Query</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Collapsible SQL with syntax highlighting */}
        <Collapsible open={sqlOpen} onOpenChange={setSqlOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/80"
            >
              <ChevronDown size={14} className={`transition-transform ${sqlOpen ? 'rotate-0' : '-rotate-90'}`} />
              SQL Query
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-1 overflow-hidden rounded-md border border-border">
              <SyntaxHighlighter
                language="sql"
                style={oneLight}
                customStyle={{
                  margin: 0,
                  padding: '0.75rem',
                  background: 'transparent',
                  fontSize: '0.8125rem',
                }}
              >
                {workflow.querySql}
              </SyntaxHighlighter>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Settings grid */}
        <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Cursor Column</p>
            <p className="font-mono text-xs">{workflow.cursorColumn ?? '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Entity Key</p>
            <p className="font-mono text-xs">{workflow.entityKeyColumn ?? '\u2014'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Dedup TTL</p>
            <p className="text-xs">{workflow.deliveryTtlHours}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Max Actions/Run</p>
            <p className="text-xs">{workflow.maxActionsPerRun}</p>
          </div>
        </div>

        {/* Parameters */}
        {workflow.parameters.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Parameters</p>
            <div className="flex flex-wrap gap-2">
              {workflow.parameters.map((p) => (
                <Badge key={p.name} variant="secondary" className="font-mono text-[0.625rem]">
                  {`{{${p.name}}}`}
                  {p.default ? ` = ${p.default}` : ''}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
